import { 
  Container, 
  Typography, 
  Box,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import QuestionRenderer from '../components/survey/QuestionRenderer';
import { surveyService, generateSessionId } from '../services/api';

const SurveyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [duplicateReason, setDuplicateReason] = useState(null);
  
  // Form state
  const [answers, setAnswers] = useState({});
  const [respondentInfo, setRespondentInfo] = useState({ name: '', email: '' });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setLoading(true);
        const response = await surveyService.getById(id);
        
        if (response && response.data) {
          setSurvey(response.data);
          
          // Check if already submitted (for anonymous surveys, check sessionId only on load)
          if (!response.data.allowMultipleResponses && response.data.isAnonymous) {
            const sessionId = generateSessionId();
            const result = await surveyService.checkExistingResponse(id, sessionId, null, true);
            setAlreadySubmitted(result.exists);
            setDuplicateReason(result.reason);
          }
        } else {
          setError('Survey not found');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching survey:', err);
        setError('Failed to load survey. Please try again later.');
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [id]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    // Clear validation error when user answers
    if (validationErrors[questionId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const handleRespondentChange = (field, value) => {
    setRespondentInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!survey?.questions) return true;
    
    survey.questions.forEach(question => {
      if (question.isRequired) {
        const answer = answers[question.id];
        if (answer === undefined || answer === '' || (Array.isArray(answer) && answer.length === 0)) {
          errors[question.id] = 'This question is required';
        }
      }
      
      // Validate number ranges
      if (question.questionType === 'number' && answers[question.id]) {
        const val = Number(answers[question.id]);
        if (question.minValue !== null && val < question.minValue) {
          errors[question.id] = `Minimum value is ${question.minValue}`;
        }
        if (question.maxValue !== null && val > question.maxValue) {
          errors[question.id] = `Maximum value is ${question.maxValue}`;
        }
      }
    });
    
    // Validate respondent info if not anonymous
    if (!survey.isAnonymous) {
      if (!respondentInfo.name.trim()) {
        errors.respondentName = 'Name is required';
      }
      if (!respondentInfo.email.trim()) {
        errors.respondentEmail = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(respondentInfo.email)) {
        errors.respondentEmail = 'Please enter a valid email address';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const sessionId = generateSessionId();
      
      // For non-anonymous surveys, check if email already submitted before proceeding
      if (!survey.isAnonymous && !survey.allowMultipleResponses) {
        const duplicateCheck = await surveyService.checkExistingResponse(
          id, 
          sessionId, 
          respondentInfo.email, 
          false
        );
        
        if (duplicateCheck.exists) {
          if (duplicateCheck.reason === 'email') {
            setError('This email address has already submitted a response to this survey.');
          } else {
            setError('You have already submitted a response to this survey from this device.');
          }
          setSubmitting(false);
          return;
        }
      }
      
      const result = await surveyService.submitResponse(
        id,
        answers,
        survey.isAnonymous ? {} : respondentInfo,
        sessionId
      );
      
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Failed to submit survey');
      }
    } catch (err) {
      console.error('Error submitting survey:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isExpired = survey?.endDate && new Date(survey.endDate) < new Date();

  if (loading) {
    return (
      <>
        <Hero 
          title="Loading Survey..." 
          subtitle=""
          imageUrl="/images/hills-bg.jpeg"
        />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (error && !survey) {
    return (
      <>
        <Hero 
          title="Survey" 
          subtitle=""
          imageUrl="/images/hills-bg.jpeg"
        />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Alert severity="error">{error}</Alert>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/surveys')}
            sx={{ mt: 2 }}
          >
            Back to Surveys
          </Button>
        </Container>
      </>
    );
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Thank You | Brynleigh Estates</title>
        </Helmet>
        <Hero 
          title="Thank You!" 
          subtitle="Your response has been recorded"
          imageUrl="/images/hills-bg.jpeg"
        />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom color="success.main">
                Survey Submitted Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Thank you for taking the time to complete this survey. 
                Your feedback helps us serve our community better.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate('/surveys')}
              >
                Back to Surveys
              </Button>
            </Paper>
          </motion.div>
        </Container>
      </>
    );
  }

  if (alreadySubmitted) {
    return (
      <>
        <Helmet>
          <title>{survey?.title || 'Survey'} | Brynleigh Estates</title>
        </Helmet>
        <Hero 
          title={survey?.title || 'Survey'} 
          subtitle=""
          imageUrl="/images/hills-bg.jpeg"
        />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="info.main">
              You've Already Submitted This Survey
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {duplicateReason === 'email' 
                ? 'A response has already been submitted with your email address.'
                : 'Our records show you have already completed this survey from this device.'}
              {' '}Thank you for your participation!
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/surveys')}
            >
              Back to Surveys
            </Button>
          </Paper>
        </Container>
      </>
    );
  }

  if (isExpired) {
    return (
      <>
        <Helmet>
          <title>{survey?.title || 'Survey'} | Brynleigh Estates</title>
        </Helmet>
        <Hero 
          title={survey?.title || 'Survey'} 
          subtitle=""
          imageUrl="/images/hills-bg.jpeg"
        />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="warning.main">
              This Survey Has Closed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The deadline for this survey has passed. Thank you for your interest.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/surveys')}
            >
              Back to Surveys
            </Button>
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{survey?.title || 'Survey'} | Brynleigh Estates</title>
        <meta name="description" content={`Complete the ${survey?.title} survey for Brynleigh Estates.`} />
      </Helmet>
      
      <Hero 
        title={survey?.title || 'Survey'} 
        subtitle={survey?.isAnonymous ? 'Anonymous Survey' : ''}
        imageUrl="/images/hills-bg.jpeg"
      />
      
      <Container maxWidth="md" sx={{ py: 6 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper sx={{ p: { xs: 2, md: 4 } }}>
            {survey?.description && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ 
                  '& a': { color: 'primary.main' },
                  '& p': { my: 1 }
                }}>
                  <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                    {survey.description}
                  </ReactMarkdown>
                </Box>
                <Divider sx={{ mt: 3 }} />
              </Box>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}
            
            <form onSubmit={handleSubmit}>
              {/* Respondent Info (if not anonymous) */}
              {!survey?.isAnonymous && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Your Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Name"
                      value={respondentInfo.name}
                      onChange={(e) => handleRespondentChange('name', e.target.value)}
                      required
                      fullWidth
                      error={!!validationErrors.respondentName}
                      helperText={validationErrors.respondentName}
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={respondentInfo.email}
                      onChange={(e) => handleRespondentChange('email', e.target.value)}
                      required
                      fullWidth
                      error={!!validationErrors.respondentEmail}
                      helperText={validationErrors.respondentEmail}
                    />
                  </Box>
                  <Divider sx={{ mt: 3 }} />
                </Box>
              )}
              
              {/* Questions */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Questions
                </Typography>
                {survey?.questions?.map((question, index) => (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <QuestionRenderer
                      question={question}
                      value={answers[question.id]}
                      onChange={handleAnswerChange}
                      error={validationErrors[question.id]}
                    />
                  </motion.div>
                ))}
              </Box>
              
              {/* Submit */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/surveys')}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Survey'}
                </Button>
              </Box>
            </form>
          </Paper>
        </motion.div>
      </Container>
    </>
  );
};

export default SurveyForm;
